import React from 'react';
import { Sparkles } from 'lucide-react';
import { CURRENCIES } from '../../constants';
import AddonFeaturesList from './AddonFeaturesList';
import { COMMON_ADDON_SUGGESTIONS, AddonTemplate } from './addonTemplates';

interface AddonFormProps {
  name: string;
  setName: (val: string) => void;
  addonId: string;
  setAddonId: (val: string) => void;
  setIsCustomId: (val: boolean) => void;
  price: number | string;
  setPrice: (val: number | string) => void;
  currency: string;
  setCurrency: (val: string) => void;
  description: string;
  setDescription: (val: string) => void;
  features: string[];
  onAddFeature: (text?: string) => void;
  onUpdateFeature: (index: number, val: string) => void;
  onRemoveFeature: (index: number) => void;
  onSelectTemplate: (tpl: AddonTemplate) => void;
  editingId: string | null;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

const AddonForm: React.FC<AddonFormProps> = ({
  name,
  setName,
  addonId,
  setAddonId,
  setIsCustomId,
  price,
  setPrice,
  currency,
  setCurrency,
  description,
  setDescription,
  features,
  onAddFeature,
  onUpdateFeature,
  onRemoveFeature,
  onSelectTemplate,
  editingId,
  onSubmit,
  onCancel,
}) => {
  const currencySymbol = CURRENCIES.find(c => c.code === currency)?.symbol || '₹';

  return (
    <form onSubmit={onSubmit} className="p-5 bg-indigo-50/50 dark:bg-indigo-950/30 rounded-2xl border border-indigo-100 dark:border-indigo-800/60 space-y-4 animate-in fade-in">
      <div className="flex justify-between items-center">
        <h4 className="text-xs font-black uppercase tracking-wider text-indigo-900 dark:text-indigo-200">
          {editingId ? 'Edit Add-on' : 'Create New Add-on'}
        </h4>
        <button type="button" onClick={onCancel} className="text-xs font-bold text-slate-400 hover:text-slate-600">
          Cancel
        </button>
      </div>

      {/* 1. Name & 2. ID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
            Add-on Name <span className="text-red-500">*</span>
          </label>
          <input
            required
            type="text"
            placeholder="e.g. Priority Support"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
            Add-on ID <span className="text-red-500">*</span>
          </label>
          <input
            required
            type="text"
            placeholder="e.g. addon_priority_support"
            value={addonId}
            disabled={!!editingId}
            onChange={e => {
              setIsCustomId(true);
              setAddonId(e.target.value);
            }}
            className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-mono text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          />
        </div>
      </div>

      {/* 3. Currency, Price & Short Description */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="space-y-1">
          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
            Currency
          </label>
          <select
            value={currency}
            onChange={e => setCurrency(e.target.value)}
            className="w-full px-2.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {CURRENCIES.map(c => (
              <option key={c.code} value={c.code}>
                {c.code} ({c.symbol})
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
            Price / Month <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-slate-400 font-bold text-xs">{currencySymbol}</span>
            <input
              required
              type="number"
              min="0"
              step="any"
              placeholder="29"
              value={price}
              onChange={e => setPrice(e.target.value)}
              className="w-full pl-7 pr-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="sm:col-span-2 space-y-1">
          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
            Short Description
          </label>
          <input
            type="text"
            placeholder="Brief overview of this add-on"
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* 4. Multiple Features Section with Add Feature Button */}
      <div className="pt-2 border-t border-indigo-100/70 dark:border-indigo-800/50">
        <AddonFeaturesList
          features={features}
          onAddFeature={onAddFeature}
          onUpdateFeature={onUpdateFeature}
          onRemoveFeature={onRemoveFeature}
        />
      </div>

      {/* Quick Templates */}
      {!editingId && (
        <div className="pt-1">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-indigo-500" /> Quick Add-on Templates:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {COMMON_ADDON_SUGGESTIONS.map(sug => (
              <button
                key={sug.name}
                type="button"
                onClick={() => onSelectTemplate(sug)}
                className="text-[11px] px-2.5 py-1 rounded-lg bg-white dark:bg-slate-800 border border-indigo-100 dark:border-indigo-800 hover:border-indigo-400 text-slate-700 dark:text-slate-200 font-medium transition-colors"
              >
                + {sug.name} (${sug.price}/mo)
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-indigo-100 dark:border-indigo-800">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
          Cancel
        </button>
        <button type="submit" className="px-4 py-1.5 text-xs font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm">
          {editingId ? 'Update Add-on' : 'Save Add-on'}
        </button>
      </div>
    </form>
  );
};

export default AddonForm;
